(() => {
  'use strict';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ---------------- money ---------------- */
  function formatMoney(cents, format) {
    format = format || '${{amount}}';
    const value = (typeof cents === 'string' ? parseInt(cents, 10) : cents) || 0;
    const fmt = (n, dec, thou, sep) => {
      const fixed = (n / 100).toFixed(dec);
      const [whole, frac] = fixed.split('.');
      const w = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thou);
      return frac ? w + sep + frac : w;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0, ',', '.');
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        default: return fmt(value, 2, ',', '.');
      }
    });
  }

  /* ---------------- marquee ---------------- */
  function initMarquees(scope = document) {
    scope.querySelectorAll('[data-cx-marquee]').forEach((el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = '1';
      const track = el.querySelector('.cx-marquee__track');
      if (!track) return;
      // make sure one track is wider than the viewport, then clone once for a seamless loop
      let guard = 0;
      while (track.scrollWidth < el.clientWidth * 1.1 && guard < 8) {
        [...track.children].forEach((c) => track.appendChild(c.cloneNode(true)));
        guard++;
      }
      const clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a,button').forEach((a) => a.setAttribute('tabindex', '-1'));
      el.appendChild(clone);
      const speed = parseFloat(el.dataset.speed || '60'); // px per second
      const dur = Math.max(8, track.scrollWidth / speed) * (reduceMotion ? 1.8 : 1);
      el.style.setProperty('--dur', dur + 's');
      if (el.dataset.direction === 'right') {
        el.querySelectorAll('.cx-marquee__track').forEach((t) => (t.style.animationDirection = 'reverse'));
      }
    });
  }

  /* ---------------- hero load moment ---------------- */
  function initHero(scope = document) {
    scope.querySelectorAll('.cx-hero').forEach((hero) => {
      if (reduceMotion) return hero.classList.add('is-ready');
      requestAnimationFrame(() => requestAnimationFrame(() => hero.classList.add('is-ready')));
    });
  }


  /* ---------------- image viewer (full-screen, uncropped, zoomable) ---------------- */
  const cxViewer = {
    el: null, img: null, stage: null, items: [], i: 0, zoomed: false,
    build() {
      if (this.el) return;
      const d = document.createElement('dialog');
      d.className = 'cx cx-viewer';
      d.setAttribute('aria-label', 'Image viewer');
      d.innerHTML = `
        <div class="cx-viewer__stage" data-stage><img class="cx-viewer__img" alt="" data-img draggable="false"></div>
        <button type="button" class="cx-viewer__btn cx-viewer__close" data-v-close aria-label="Close viewer"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="m6 6 12 12M18 6 6 18"/></svg></button>
        <button type="button" class="cx-viewer__btn cx-viewer__prev" data-v-prev aria-label="Previous image"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="m15 5-7 7 7 7"/></svg></button>
        <button type="button" class="cx-viewer__btn cx-viewer__next" data-v-next aria-label="Next image"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="m9 5 7 7-7 7"/></svg></button>
        <div class="cx-viewer__bar"><span data-v-count></span><span class="cx-viewer__hint" data-v-hint>Tap image to zoom</span></div>`;
      document.body.appendChild(d);
      this.el = d; this.img = d.querySelector('[data-img]'); this.stage = d.querySelector('[data-stage]');
      this.count = d.querySelector('[data-v-count]'); this.hint = d.querySelector('[data-v-hint]');
      d.querySelector('[data-v-close]').addEventListener('click', () => d.close());
      d.querySelector('[data-v-prev]').addEventListener('click', () => this.go(this.i - 1));
      d.querySelector('[data-v-next]').addEventListener('click', () => this.go(this.i + 1));
      d.addEventListener('close', () => { this.unzoom(); document.documentElement.classList.remove('cx-locked'); });
      d.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.i + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.i - 1); }
      });
      // click / tap toggles zoom at the pointer position
      this.img.addEventListener('click', (e) => { if (this.moved) return; this.zoomed ? this.unzoom() : this.zoom(e); });
      // desktop: pan by moving the cursor while zoomed
      this.stage.addEventListener('mousemove', (e) => {
        if (!this.zoomed || !window.matchMedia('(hover: hover)').matches) return;
        const r = this.stage.getBoundingClientRect();
        const fx = (e.clientX - r.left) / r.width, fy = (e.clientY - r.top) / r.height;
        this.stage.scrollLeft = fx * (this.stage.scrollWidth - r.width);
        this.stage.scrollTop = fy * (this.stage.scrollHeight - r.height);
      });
      // touch: swipe to change image when not zoomed (zoomed = native scroll pans)
      let sx = 0, sy = 0;
      this.stage.addEventListener('touchstart', (e) => { this.moved = false; sx = e.touches[0].clientX; sy = e.touches[0].clientY; }, { passive: true });
      this.stage.addEventListener('touchmove', () => { this.moved = true; }, { passive: true });
      this.stage.addEventListener('touchend', (e) => {
        if (this.zoomed) { setTimeout(() => (this.moved = false), 50); return; }
        const dx = e.changedTouches[0].clientX - sx, dy = e.changedTouches[0].clientY - sy;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) this.go(this.i + (dx < 0 ? 1 : -1));
        setTimeout(() => (this.moved = false), 50);
      });
    },
    open(items, i) {
      this.build(); this.items = items;
      const multi = items.length > 1;
      this.el.querySelector('[data-v-prev]').hidden = !multi;
      this.el.querySelector('[data-v-next]').hidden = !multi;
      this.go(i);
      if (!this.el.open) this.el.showModal();
      document.documentElement.classList.add('cx-locked');
    },
    go(i) {
      const n = this.items.length; if (!n) return;
      this.i = (i + n) % n; this.unzoom();
      const it = this.items[this.i];
      this.img.src = it.src; this.img.alt = it.alt || '';
      this.count.textContent = n > 1 ? `${this.i + 1} / ${n}` : '';
      // preload neighbours
      [this.i + 1, this.i - 1].forEach((k) => { const x = this.items[(k + n) % n]; if (x) { const im = new Image(); im.src = x.src; } });
    },
    zoom(e) {
      const r = this.stage.getBoundingClientRect();
      const ir = this.img.getBoundingClientRect();
      const fx = Math.min(1, Math.max(0, (e.clientX - ir.left) / ir.width));
      const fy = Math.min(1, Math.max(0, (e.clientY - ir.top) / ir.height));
      const scale = Math.max(2.4, (this.img.naturalWidth || 2400) / ir.width > 2.4 ? 2.4 : 2);
      this.zoomed = true;
      this.el.classList.add('is-zoomed');
      this.img.style.width = Math.round(ir.width * scale) + 'px';
      requestAnimationFrame(() => {
        this.stage.scrollLeft = fx * this.stage.scrollWidth - r.width / 2;
        this.stage.scrollTop = fy * this.stage.scrollHeight - r.height / 2;
      });
      this.hint.textContent = 'Tap to zoom out';
    },
    unzoom() {
      if (!this.el) return;
      this.zoomed = false;
      this.el.classList.remove('is-zoomed');
      this.img.style.width = '';
      this.stage.scrollLeft = 0; this.stage.scrollTop = 0;
      this.hint.textContent = window.matchMedia('(hover: hover)').matches ? 'Click to zoom' : 'Tap to zoom';
    },
  };
  window.cxViewer = cxViewer;

  /* ---------------- gallery ---------------- */
  class Gallery {
    constructor(el) {
      this.el = el;
      this.track = el.querySelector('[data-track]');
      this.slides = [...el.querySelectorAll('[data-slide]')];
      this.thumbs = [...el.querySelectorAll('[data-thumb]')];
      this.dots = [...el.querySelectorAll('[data-dot]')];
      this.counter = el.querySelector('[data-counter]');
      this.index = 0;
      const go = (i) => () => this.goTo(i);
      this.thumbs.forEach((t, i) => t.addEventListener('click', go(i)));
      this.dots.forEach((d, i) => d.addEventListener('click', go(i)));
      let raf;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          if (i !== this.index) this.setActive(i);
        });
      }, { passive: true });
      const items = () => this.slides.filter((sl) => sl.dataset.full).map((sl) => ({ src: sl.dataset.full, alt: sl.dataset.alt }));
      this.slides.forEach((sl, n) => sl.addEventListener('click', () => { if (sl.dataset.full) cxViewer.open(items(), n); }));
      const zb = el.querySelector('[data-zoom]');
      if (zb) zb.addEventListener('click', () => cxViewer.open(items(), this.index));
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(Math.min(this.index + 1, this.slides.length - 1)); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(Math.max(this.index - 1, 0)); }
      });
    }
    goTo(i) {
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
      this.setActive(i);
    }
    goToMedia(mediaId) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i > -1) this.goTo(i);
    }
    setActive(i) {
      this.index = i;
      this.thumbs.forEach((t, n) => t.setAttribute('aria-current', n === i ? 'true' : 'false'));
      this.dots.forEach((d, n) => d.setAttribute('aria-current', n === i ? 'true' : 'false'));
      if (this.counter) this.counter.textContent = `${i + 1} / ${this.slides.length}`;
    }
  }


  /* ---------------- sheets: search / menu / cart ---------------- */
  const esc = (t) => String(t == null ? '' : t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const imgUrl = (u, w) => { if (!u) return ''; try { const x = new URL(u, location.origin); x.searchParams.set('width', w); return x.toString(); } catch (_) { return u; } };

  function openSheet(id) {
    const d = document.getElementById(id);
    if (!d || typeof d.showModal !== 'function') return false;
    document.querySelectorAll('dialog.cx-sheet[open]').forEach((o) => o !== d && o.close());
    if (!d.open) d.showModal();
    document.documentElement.classList.add('cx-locked');
    d.dispatchEvent(new CustomEvent('cx:open'));
    return true;
  }
  function initSheets() {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-open-sheet]');
      if (opener) {
        if (openSheet(opener.dataset.openSheet)) e.preventDefault();
        return;
      }
      if (e.target.closest('[data-close-sheet]')) { e.target.closest('dialog').close(); return; }
      if (e.target.matches('dialog.cx-sheet')) {
        const r = e.target.getBoundingClientRect();
        const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
        if (!inside) e.target.close();
      }
    });
    document.querySelectorAll('dialog.cx-sheet').forEach((d) => {
      d.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); d.close(); } });
      d.addEventListener('close', () => {
        if (!document.querySelector('dialog.cx-sheet[open]')) document.documentElement.classList.remove('cx-locked');
      });
    });
    const header = document.querySelector('[data-cx-header]');
    if (header) {
      const announce = document.querySelector('.cx-announce');
      const setH = () => {
        document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
        document.documentElement.style.setProperty('--cx-announce-h', (announce ? announce.offsetHeight : 0) + 'px');
      };
      setH(); window.addEventListener('resize', setH, { passive: true });
    }
  }

  /* ---------------- predictive search ---------------- */
  function initSearch() {
    const d = document.getElementById('cx-search');
    if (!d) return;
    const input = d.querySelector('[data-search-input]');
    const out = d.querySelector('[data-search-results]');
    const fmt = (document.getElementById('cx-cart') || {}).dataset?.moneyFormat;
    d.addEventListener('cx:open', () => setTimeout(() => input.focus(), 30));
    let timer, ctrl;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) { out.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        if (ctrl) ctrl.abort();
        ctrl = new AbortController();
        try {
          const url = `${root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
          const data = await (await fetch(url, { signal: ctrl.signal })).json();
          const items = (data.resources && data.resources.results && data.resources.results.products) || [];
          if (!items.length) { out.innerHTML = `<p class="cx-search__none">No results for “${esc(q)}”.</p>`; return; }
          const price = (p) => {
            const v = p.price_min != null ? p.price_min : p.price;
            const cents = typeof v === 'string' && v.includes('.') ? Math.round(parseFloat(v) * 100) : Number(v);
            return formatMoney(cents, fmt);
          };
          out.innerHTML = `<div class="cx-search__grid">${items.map((p) => `
            <a class="cx-card" href="${esc(p.url)}">
              <span class="cx-card__media">${p.image ? `<img class="cx-card__img" src="${esc(imgUrl(p.image, 400))}" alt="" loading="lazy">` : ''}</span>
              <span class="cx-card__meta"><span class="cx-card__vendor">${esc(p.vendor)}</span><span class="cx-card__title">${esc(p.title)}</span><span class="cx-card__price">${price(p)}</span></span>
            </a>`).join('')}</div>
            <a class="cx-textlink cx-search__all" href="${root}search?type=product&q=${encodeURIComponent(q)}">View all results for “${esc(q)}”</a>`;
        } catch (err) { if (err.name !== 'AbortError') out.innerHTML = ''; }
      }, 180);
    });
  }

  /* ---------------- cart drawer ---------------- */
  const cxCart = {
    el: null, fmt: null,
    init() {
      this.el = document.getElementById('cx-cart');
      if (!this.el) return;
      this.fmt = this.el.dataset.moneyFormat;
      this.items = this.el.querySelector('[data-cart-items]');
      this.foot = this.el.querySelector('[data-cart-foot]');
      this.el.addEventListener('cx:open', () => this.refresh());
      this.items.addEventListener('click', (e) => {
        const b = e.target.closest('[data-line-qty],[data-line-remove]');
        if (!b) return;
        const line = b.closest('.cx-line');
        const q = b.hasAttribute('data-line-remove') ? 0 : Math.max(0, Number(line.dataset.qty) + Number(b.dataset.lineQty));
        this.change(line, q);
      });
    },
    async fetchCart() { return (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json(); },
    async refresh(cart) { try { this.render(cart || await this.fetchCart()); } catch (_) {} },
    async open(cart) { if (!this.el) return false; openSheet('cx-cart'); await this.refresh(cart); return true; },
    async change(line, quantity) {
      line.classList.add('is-busy');
      try {
        const res = await fetch(root + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: line.dataset.key, quantity }) });
        const json = await res.json();
        if (!res.ok) { line.classList.remove('is-busy'); this.flash(line, json.description || json.message); return; }
        this.render(json);
      } catch (_) { line.classList.remove('is-busy'); }
    },
    flash(line, msg) {
      let n = line.querySelector('.cx-line__msg');
      if (!n) { n = document.createElement('p'); n.className = 'cx-line__msg cx-form__error'; line.querySelector('.cx-line__info').appendChild(n); }
      n.textContent = msg || 'No more available.';
    },
    setCount(n) {
      document.querySelectorAll('[data-cart-count]').forEach((c) => { c.textContent = n; c.hidden = n === 0; });
      document.querySelectorAll('.cx-header__cart').forEach((a) => a.setAttribute('aria-label', `Cart, ${n} items`));
    },
    render(cart) {
      this.setCount(cart.item_count);
      if (!this.items) return;
      if (!cart.item_count) {
        this.items.innerHTML = this.el.querySelector('[data-cart-empty]').innerHTML;
        this.foot.hidden = true;
        return;
      }
      this.items.innerHTML = cart.items.map((it) => {
        const variant = it.variant_title && it.variant_title !== 'Default Title' ? `<span class="cx-line__variant">${esc(it.variant_title)}</span>` : '';
        return `<div class="cx-line" data-key="${esc(it.key)}" data-qty="${it.quantity}">
          <a class="cx-line__img" href="${esc(it.url)}">${it.image ? `<img src="${esc(imgUrl(it.image, 240))}" alt="" loading="lazy">` : ''}</a>
          <div class="cx-line__info">
            <span class="cx-line__vendor">${esc(it.vendor)}</span>
            <a class="cx-line__title" href="${esc(it.url)}">${esc(it.product_title)}</a>
            ${variant}
            <div class="cx-line__row">
              <div class="cx-qty"><button type="button" data-line-qty="-1" aria-label="Decrease quantity">&minus;</button><span>${it.quantity}</span><button type="button" data-line-qty="1" aria-label="Increase quantity">+</button></div>
              <span>${formatMoney(it.final_line_price, this.fmt)}</span>
            </div>
            <button type="button" class="cx-line__remove" data-line-remove>Remove</button>
          </div>
        </div>`;
      }).join('');
      this.el.querySelector('[data-cart-subtotal]').textContent = formatMoney(cart.total_price, this.fmt);
      this.foot.hidden = false;
    },
  };
  window.cxCart = cxCart;

  /* ---------------- cart bridge (Horizon) ---------------- */
  async function refreshHorizonCart(sourceId, productId) {
    let cart = null;
    try { cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json(); } catch (_) {}
    const evt = new Event('cart:update', { bubbles: true });
    evt.detail = {
      resource: cart,
      sourceId,
      data: { itemCount: cart ? cart.item_count : 0, source: 'cx-buybox', productId },
    };
    document.dispatchEvent(evt);
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }
    return false;
  }

  /* ---------------- product form ---------------- */
  class BuyBox {
    constructor(el) {
      this.el = el;
      this.data = JSON.parse(el.querySelector('[data-cx-product-json]').textContent);
      this.form = el.querySelector('form[id^="cx-form-"]');
      this.idInput = this.form.querySelector('[data-variant-input]');
      this.atc = this.form.querySelector('[data-atc]');
      this.atcLabel = this.form.querySelector('[data-atc-label]');
      this.atcPrice = this.form.querySelector('[data-atc-price]');
      this.error = this.form.querySelector('[data-error]');
      this.priceEl = el.querySelector('[data-price]');
      this.stockEl = el.querySelector('[data-stock]');
      this.optionEls = [...el.querySelectorAll('[data-option-index]')];
      const g = el.querySelector('[data-cx-gallery]');
      this.gallery = g ? new Gallery(g) : null;
      this.variant = this.data.variants.find((v) => v.id === Number(this.idInput.value)) || this.data.variants[0];

      this.el.addEventListener('change', (e) => {
        if (e.target.closest('[data-option-index]')) this.onOptionChange();
      });
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.initQty();
      this.initBuyNow();
      this.initSizeGuide();
      this.markUnavailable();
      if (this.el.dataset.main === 'true') this.initSticky();
    }

    selectedOptions() {
      return this.optionEls.map((fs) => {
        const c = fs.querySelector('input:checked');
        return c ? c.value : null;
      });
    }

    onOptionChange() {
      const opts = this.selectedOptions();
      this.optionEls.forEach((fs, i) => {
        const lbl = fs.querySelector('[data-option-label]');
        if (lbl) lbl.textContent = opts[i] || '';
      });
      const v = this.data.variants.find((x) => x.options.every((o, i) => o === opts[i]));
      this.variant = v || null;
      this.render();
      this.markUnavailable();
    }

    markUnavailable() {
      if (!this.optionEls.length) return;
      const opts = this.selectedOptions();
      this.optionEls.forEach((fs, idx) => {
        fs.querySelectorAll('.cx-chip').forEach((chip) => {
          const val = chip.querySelector('input').value;
          const test = opts.slice(); test[idx] = val;
          const match = this.data.variants.find((x) => x.options.every((o, i) => o === test[i]));
          chip.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }

    render() {
      const v = this.variant;
      const fmt = this.data.money_format;
      if (!v) {
        this.setAtc(false, this.data.strings.unavailable);
        if (this.stockEl) this.stockEl.innerHTML = '';
        return;
      }
      this.idInput.value = v.id;
      this.setAtc(v.available, v.available ? this.data.strings.add : this.data.strings.sold_out);
      if (this.atcPrice) this.atcPrice.textContent = formatMoney(v.price, fmt);
      if (this.priceEl) {
        let html = `<span class="cx-price__now">${formatMoney(v.price, fmt)}</span>`;
        if (v.compare_at_price && v.compare_at_price > v.price) {
          html += `<s class="cx-price__was">${formatMoney(v.compare_at_price, fmt)}</s>`;
          html += `<span class="cx-price__save">${this.data.strings.save} ${formatMoney(v.compare_at_price - v.price, fmt)}</span>`;
        }
        const note = this.priceEl.querySelector('.cx-price__note');
        this.priceEl.innerHTML = html + (note ? note.outerHTML : '');
      }
      if (this.stockEl) {
        const s = this.data.strings;
        let dot = 'cx-dot', txt = s.in_stock;
        if (!v.available) { dot += ' cx-dot--out'; txt = s.sold_out; }
        else if (v.low) { dot += ' cx-dot--low'; txt = s.low_stock.replace('[count]', v.qty); }
        this.stockEl.innerHTML = `<span class="${dot}" aria-hidden="true"></span><span>${txt}</span>`;
      }
      if (v.media_id && this.gallery) this.gallery.goToMedia(v.media_id);
      if (this.el.dataset.main === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        history.replaceState(history.state, '', url.toString());
      }
      this.syncSticky();
    }

    setAtc(enabled, label) {
      this.atc.disabled = !enabled;
      if (this.atcLabel) this.atcLabel.textContent = label;
      if (this.buyNow) {
        if (enabled) this.buyNow.removeAttribute('aria-disabled'); else this.buyNow.setAttribute('aria-disabled', 'true');
      }
    }

    initBuyNow() {
      this.buyNow = this.el.querySelector('[data-buy-now]');
      if (!this.buyNow) return;
      this.buyNow.addEventListener('click', (e) => {
        e.preventDefault();
        if (!this.variant || !this.variant.available) return;
        const qtyInput = this.form.querySelector('input[name="quantity"]');
        const qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
        this.buyNow.classList.add('is-loading');
        window.location.href = `${root}cart/${this.variant.id}:${qty}`;
      });
    }

    initQty() {
      const wrap = this.form.querySelector('[data-qty]');
      if (!wrap) return;
      const input = wrap.querySelector('input');
      wrap.addEventListener('click', (e) => {
        const b = e.target.closest('button[data-step]');
        if (!b) return;
        const n = Math.max(1, (parseInt(input.value, 10) || 1) + Number(b.dataset.step));
        input.value = n;
      });
    }

    initSizeGuide() {
      const trigger = this.el.querySelector('[data-size-guide-open]');
      const dialog = this.el.querySelector('[data-size-guide]');
      if (!trigger || !dialog || typeof dialog.showModal !== 'function') return;
      trigger.addEventListener('click', () => dialog.showModal());
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog || e.target.closest('[data-close]')) dialog.close();
      });
    }

    async onSubmit(e) {
      e.preventDefault();
      if (!this.variant || !this.variant.available) return;
      this.error.hidden = true;
      this.atc.classList.add('is-loading');
      if (this.stickyBtn) this.stickyBtn.classList.add('is-loading');
      try {
        const res = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(this.form),
        });
        const json = await res.json();
        if (!res.ok || json.status) {
          let msg = json.description || json.message || this.data.strings.error;
          if (/sold out|in your cart|can only add|can't add/i.test(msg)) {
            try {
              const cart = await cxCart.fetchCart();
              const inCart = cart.items.find((i) => i.variant_id === this.variant.id);
              msg = inCart ? 'The last one is already in your cart.' : 'Sorry, this option just sold out.';
              if (inCart) { this.error.textContent = msg; this.error.hidden = false; cxCart.open(cart); return; }
            } catch (_) {}
          }
          throw new Error(msg);
        }
        let opened = false;
        if (cxCart.el) opened = await cxCart.open();
        else opened = await refreshHorizonCart(this.el.id, String(this.data.id));
        if (!opened) window.location.href = root + 'cart';
      } catch (err) {
        this.error.textContent = err.message || this.data.strings.error;
        this.error.hidden = false;
      } finally {
        this.atc.classList.remove('is-loading');
        if (this.stickyBtn) this.stickyBtn.classList.remove('is-loading');
      }
    }

    initSticky() {
      const bar = document.querySelector(`[data-cx-sticky="${this.el.id}"]`);
      if (!bar) return;
      this.sticky = bar;
      this.stickyBtn = bar.querySelector('[data-sticky-atc]');
      this.stickyPrice = bar.querySelector('[data-sticky-price]');
      this.stickyBtn.addEventListener('click', () => {
        if (this.variant && this.variant.available) this.form.requestSubmit ? this.form.requestSubmit(this.atc) : this.atc.click();
        else this.el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
      });
      const footer = document.querySelector('footer');
      let ticking = false;
      const update = () => {
        ticking = false;
        const passed = this.atc.getBoundingClientRect().bottom < 0;
        const atFooter = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
        bar.classList.toggle('is-visible', passed && !atFooter);
      };
      window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
      bar.removeAttribute('hidden');
      this.syncSticky();
    }

    syncSticky() {
      if (!this.sticky || !this.variant) return;
      this.stickyPrice.textContent = formatMoney(this.variant.price, this.data.money_format);
      this.stickyBtn.disabled = !this.variant.available;
      this.stickyBtn.querySelector('span').textContent = this.variant.available ? this.data.strings.add : this.data.strings.sold_out;
    }
  }

  function initBuyBoxes(scope = document) {
    scope.querySelectorAll('[data-cx-buybox]').forEach((el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = '1';
      try { new BuyBox(el); } catch (err) { console.error('[cx] buy box', err); }
    });
  }

  /* ---------------- hotspots ---------------- */
  function initHotspots(scope = document) {
    scope.querySelectorAll('[data-cx-hotspots]').forEach((el) => {
      const pins = [...el.querySelectorAll('[data-pin]')];
      const items = [...el.querySelectorAll('[data-pin-item]')];
      const activate = (i) => {
        pins.forEach((p, n) => { p.classList.toggle('is-active', n === i); p.setAttribute('aria-pressed', n === i ? 'true' : 'false'); });
        items.forEach((it, n) => it.classList.toggle('is-active', n === i));
      };
      pins.forEach((p, i) => { p.addEventListener('mouseenter', () => activate(i)); p.addEventListener('click', () => activate(i)); p.addEventListener('focus', () => activate(i)); });
      items.forEach((it, i) => it.addEventListener('mouseenter', () => activate(i)));
      el.addEventListener('mouseleave', () => activate(-1));
    });
  }

  /* ---------------- reviews: expand, don't paginate ---------------- */
  function initReviews(scope = document) {
    scope.querySelectorAll('[data-cx-reviews]').forEach((el) => {
      const btn = el.querySelector('[data-more]');
      if (!btn) return;
      btn.addEventListener('click', () => {
        const hidden = [...el.querySelectorAll('.cx-review[hidden]')];
        hidden.slice(0, Number(btn.dataset.step || 6)).forEach((r) => r.removeAttribute('hidden'));
        if (!el.querySelector('.cx-review[hidden]')) btn.remove();
      });
    });
  }


  /* ---------------- related products (Shopify recommendations) ---------------- */
  function initRecs(scope = document) {
    scope.querySelectorAll('[data-cx-recs][data-url]').forEach(async (el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = '1';
      try {
        const html = await (await fetch(el.dataset.url)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-cx-recs]');
        if (fresh && fresh.querySelector('.cx-card')) el.innerHTML = fresh.innerHTML;
        else el.closest('.cx-grid-section')?.remove();
      } catch (_) { el.closest('.cx-grid-section')?.remove(); }
    });
  }

  /* ---------------- collection sort ---------------- */
  function initSort(scope = document) {
    scope.querySelectorAll('[data-cx-sort]').forEach((sel) => {
      sel.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sel.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    });
  }

  let sheetsReady = false;
  function initAll(scope) {
    if (!sheetsReady) { sheetsReady = true; initSheets(); initSearch(); cxCart.init(); }
    initMarquees(scope); initHero(scope); initBuyBoxes(scope); initHotspots(scope); initReviews(scope); initRecs(scope); initSort(scope);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document));
  else initAll(document);

  // theme editor support
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();
